import React, { useEffect, useState, useCallback } from 'react';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { Breakout } from '../../api/protosCompiled/breakout/breakout_pb';
import {
  useFetchChangeOrderBreakouts,
  useFetchEstimateBreakouts,
} from '../../hooks/useFetchBreakout';
import { TakeoffAssembly } from '../../types/AssemblyItemType';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  useTheme,
  alpha,
  Box,
  Typography,
} from '@mui/material';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { EstimateBreakoutMap } from '../../api/protosCompiled/estimateAssembly/estimateAssembly_pb';
import { ChangeOrderBreakoutMap } from '../../api/protosCompiled/changeOrderAssembly/changeOrderAssembly_pb';

type BreakoutSelectorProps = {
  currentTakeoffAssembly?: TakeoffAssembly;
  context?: contextType;
  size?: 'small' | 'medium';
};

type contextType = 'takeoffPallette' | 'createAssembly';

// Create a type for our localStorage structure
type SavedBreakouts = {
  [key: string]: string; // Maps estimateId/changeOrderId to breakoutId
};

// Extended breakout type that includes typical breakout information
type ExtendedBreakout = Breakout.AsObject & {
  isTypical?: boolean;
  standardBreakoutId?: string;
  typicalBreakoutId?: string;
};

// Extended breakout type for the selectedBreakout state
type ExtendedSelectedBreakout = Breakout.AsObject & {
  _typicalBreakoutId?: string;
  _isTypical?: boolean;
};

const BreakoutSelector = ({
  currentTakeoffAssembly,
  context = 'takeoffPallette',
  size = 'medium',
}: BreakoutSelectorProps) => {
  const theme = useTheme();
  const selectedEstimate = useStore(useUnityBuildStore, state => state.selectedEstimate);
  const selectedChangeOrder = useStore(useUnityBuildStore, state => state.selectedChangeOrder);
  const setSelectedBreakout = useStore(useUnityBuildStore, state => state.setSelectedBreakout);
  const { setCurrentTakeoffAssembly } = useStore(useAssemblyStore);

  const [localBreakouts, setLocalBreakouts] = useState<ExtendedBreakout[]>([]);
  const [selectedBreakoutId, setSelectedBreakoutId] = useState<string>('');

  // Get the current context ID (either estimateId or changeOrderId)
  const contextId = selectedEstimate?.estimateid || selectedChangeOrder?.changeorderid || '';

  // Helper functions for localStorage
  const saveBreakoutToLocalStorage = useCallback((contextId: string, breakoutId: string) => {
    try {
      // Get existing saved breakouts or initialize empty object
      const savedBreakoutsJson = localStorage.getItem('selectedBreakouts');
      const savedBreakouts: SavedBreakouts = savedBreakoutsJson
        ? JSON.parse(savedBreakoutsJson)
        : {};

      // Update with new selection
      savedBreakouts[contextId] = breakoutId;

      // Save back to localStorage
      localStorage.setItem('selectedBreakouts', JSON.stringify(savedBreakouts));
    } catch (error) {
      console.error('Error saving breakout to localStorage:', error);
    }
  }, []);

  const getBreakoutFromLocalStorage = useCallback((contextId: string): string | null => {
    try {
      const savedBreakoutsJson = localStorage.getItem('selectedBreakouts');
      if (!savedBreakoutsJson) return null;

      const savedBreakouts: SavedBreakouts = JSON.parse(savedBreakoutsJson);
      return savedBreakouts[contextId] || null;
    } catch (error) {
      console.error('Error retrieving breakout from localStorage:', error);
      return null;
    }
  }, []);

  // Fetch breakouts from estimate or change order
  const { data: estimateBreakouts } = useFetchEstimateBreakouts(selectedEstimate?.estimateid ?? '');
  const { data: changeOrderBreakouts } = useFetchChangeOrderBreakouts(
    selectedChangeOrder?.changeorderid ?? ''
  );

  // Create combined list of standard breakouts and typical breakouts
  const combinedBreakouts = React.useMemo(() => {
    const breakouts = estimateBreakouts?.breakoutsList ?? changeOrderBreakouts?.breakoutsList ?? [];

    // Start with standard breakouts
    const result: ExtendedBreakout[] = [...breakouts];

    // Add typical breakouts as additional options
    breakouts.forEach(standardBreakout => {
      standardBreakout.typicalbreakoutsList?.forEach(typical => {
        result.push({
          ...standardBreakout,
          id: typical.id, // Use typical breakout ID
          breakoutname: `${standardBreakout.breakoutname} - ${typical.breakoutname}`,
          multiplier: typical.multiplier,
          isTypical: true,
          standardBreakoutId: standardBreakout.id,
          typicalBreakoutId: typical.id,
        } as ExtendedBreakout);
      });
    });

    return result;
  }, [estimateBreakouts, changeOrderBreakouts]);

  // Update localBreakouts when data changes
  useEffect(() => {
    setLocalBreakouts(combinedBreakouts);
  }, [combinedBreakouts]);

  // When breakouts load or context changes, try to restore from localStorage
  useEffect(() => {
    if (localBreakouts.length > 0 && contextId) {
      // First check if we should use the breakout from currentTakeoffAssembly
      if (currentTakeoffAssembly?.itemsList[0]?.breakout && context === 'createAssembly') {
        const currentBreakoutId = currentTakeoffAssembly.itemsList[0]?.breakout?.breakoutid;
        const currentTypicalBreakoutId =
          currentTakeoffAssembly.itemsList[0]?.breakout?.typicalbreakoutid;

        // If we have a typical breakout ID, we need to find the corresponding typical breakout option
        if (currentTypicalBreakoutId) {
          const typicalBreakout = localBreakouts.find(
            b => b.isTypical && b.typicalBreakoutId === currentTypicalBreakoutId
          );
          if (typicalBreakout) {
            setSelectedBreakoutId(typicalBreakout.id);
            // Find the standard breakout and set it with typical metadata
            const standardBreakout = localBreakouts.find(
              b => b.id === typicalBreakout.standardBreakoutId && !b.isTypical
            );
            if (standardBreakout) {
              setSelectedBreakout({
                ...standardBreakout,
                _typicalBreakoutId: currentTypicalBreakoutId,
                _isTypical: true,
              } as ExtendedSelectedBreakout);
            }
            return; // Exit early, don't load from localStorage
          }
        }

        // Verify this breakout still exists in our options (for standard breakouts)
        const breakoutExists = localBreakouts.some(b => b.id === currentBreakoutId);
        if (breakoutExists) {
          setSelectedBreakoutId(currentBreakoutId);
          const currentBreakout = localBreakouts.find(
            breakout => breakout.id === currentBreakoutId
          );
          if (currentBreakout) {
            setSelectedBreakout(currentBreakout);
          }
          return; // Exit early, don't load from localStorage
        }
      }

      // Try to get saved breakout from localStorage
      const savedBreakoutId = getBreakoutFromLocalStorage(contextId);
      if (savedBreakoutId) {
        // Verify the saved breakout still exists in our options
        const breakoutExists = localBreakouts.some(b => b.id === savedBreakoutId);
        if (breakoutExists) {
          setSelectedBreakoutId(savedBreakoutId);
          const savedBreakout = localBreakouts.find(breakout => breakout.id === savedBreakoutId);
          if (savedBreakout) {
            // Handle typical breakouts the same way as when they're initially selected
            if (savedBreakout.isTypical) {
              const standardBreakout = localBreakouts.find(
                b => b.id === savedBreakout.standardBreakoutId && !b.isTypical
              );
              if (standardBreakout) {
                setSelectedBreakout({
                  ...standardBreakout,
                  _typicalBreakoutId: savedBreakout.typicalBreakoutId,
                  _isTypical: true,
                } as ExtendedSelectedBreakout);
              }
            } else {
              setSelectedBreakout(savedBreakout);
            }
            return; // Exit early since we've loaded from localStorage
          }
        }
      }

      // Default fallback - select the first available breakout
      setSelectedBreakoutId(localBreakouts[0]?.id ?? '');
      if (localBreakouts[0]) {
        setSelectedBreakout(localBreakouts[0]);
      }
    }
  }, [
    localBreakouts,
    contextId,
    currentTakeoffAssembly,
    context,
    setSelectedBreakout,
    getBreakoutFromLocalStorage,
  ]);

  // Update the handle function to save to localStorage
  const handleSetSelectedBreakout = (event: SelectChangeEvent<string>) => {
    event.stopPropagation();

    const breakoutId = event.target.value;
    setSelectedBreakoutId(breakoutId);

    const selectedBreakoutItem = localBreakouts.find(breakout => breakout.id === breakoutId);

    if (selectedBreakoutItem) {
      // For typical breakouts, we need to set the selectedBreakout to the standard breakout
      // but preserve the typical breakout information for display purposes
      if (selectedBreakoutItem.isTypical) {
        // Find the standard breakout that this typical breakout references
        const standardBreakout = localBreakouts.find(
          b => b.id === selectedBreakoutItem.standardBreakoutId && !b.isTypical
        );

        if (standardBreakout) {
          // Set the selectedBreakout to the standard breakout, but with typical info
          setSelectedBreakout({
            ...standardBreakout,
            // Store the typical breakout ID for reference
            _typicalBreakoutId: selectedBreakoutItem.typicalBreakoutId,
            _isTypical: true,
          } as ExtendedSelectedBreakout);

          // Keep the selectedBreakoutId as the typical breakout ID so the dropdown shows the correct selection
          // This ensures the UI displays "Lighting - Levels 1-10 (6x)" while the data contains the standard breakout info
        }
      } else {
        // For standard breakouts, set normally
        setSelectedBreakout(selectedBreakoutItem);
      }

      // Save to localStorage if we have a valid contextId
      if (contextId) {
        saveBreakoutToLocalStorage(contextId, breakoutId);

        // Debug logging to verify the selection state
        console.log('Breakout selection updated:', {
          selectedBreakoutId: breakoutId,
          selectedBreakoutItem: {
            id: selectedBreakoutItem.id,
            name: selectedBreakoutItem.breakoutname,
            isTypical: selectedBreakoutItem.isTypical,
            standardBreakoutId: selectedBreakoutItem.standardBreakoutId,
            typicalBreakoutId: selectedBreakoutItem.typicalBreakoutId,
          },
          selectedBreakout: selectedBreakoutItem.isTypical
            ? 'Standard breakout with typical metadata'
            : 'Standard breakout',
        });
      }

      // If we're in CreateAssembly context, update all items in the assembly with the new breakout
      if (context === 'createAssembly' && currentTakeoffAssembly) {
        // Debug logging to verify ID mapping
        if (selectedBreakoutItem.isTypical) {
          console.log('Creating assembly with typical breakout:', {
            standardBreakoutId: selectedBreakoutItem.standardBreakoutId,
            typicalBreakoutId: selectedBreakoutItem.typicalBreakoutId,
            breakoutName: selectedBreakoutItem.breakoutname,
            multiplier: selectedBreakoutItem.multiplier,
          });
        }

        setCurrentTakeoffAssembly(prev => {
          if (!prev) return prev;

          return {
            ...prev,
            itemsList: prev.itemsList.map(item => ({
              ...item,
              breakout: {
                breakoutmapid: item.breakout?.breakoutmapid || crypto.randomUUID(),
                breakoutid: selectedBreakoutItem.isTypical
                  ? selectedBreakoutItem.standardBreakoutId!
                  : selectedBreakoutItem.id,
                breakoutname: selectedBreakoutItem.breakoutname,
                multiplier: selectedBreakoutItem.multiplier,
                typicalbreakoutid: selectedBreakoutItem.isTypical
                  ? selectedBreakoutItem.typicalBreakoutId
                  : '',
              } as EstimateBreakoutMap.AsObject | ChangeOrderBreakoutMap.AsObject,
            })),
          };
        });
      }
    } else {
      setSelectedBreakout(null);
    }
  };

  return (
    <Box sx={{ px: 1, py: 1 }}>
      <FormControl
        sx={{
          width: '100%',
        }}
        size={size}
        onClick={e => e.stopPropagation()}
      >
        <InputLabel
          id="breakout-select-label"
          sx={{
            color:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.7)
                : alpha(theme.palette.common.black, 0.6),
            '&.Mui-focused': {
              color: theme.palette.primary.main,
            },
          }}
        >
          Select Breakout
        </InputLabel>
        <Select
          labelId="breakout-select-label"
          onChange={handleSetSelectedBreakout}
          value={selectedBreakoutId}
          displayEmpty
          label="Select Breakout"
          sx={{
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.15)
                : alpha(theme.palette.background.paper, 0.9),
            color: theme.palette.text.primary,
            '.MuiOutlinedInput-notchedOutline': {
              borderColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.23)
                  : alpha(theme.palette.common.black, 0.23),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '& .MuiSelect-icon': {
              color:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.7)
                  : alpha(theme.palette.common.black, 0.54),
            },
          }}
          size={size}
          onClick={e => e.stopPropagation()}
          onMouseDown={e => e.stopPropagation()}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                '& .MuiMenuItem-root': {
                  color: theme.palette.text.primary,
                  py: 0.75,
                },
                '& .MuiMenuItem-root.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                },
                '& .MuiMenuItem-root:hover': {
                  backgroundColor: alpha(theme.palette.action.hover, 0.1),
                },
                '& .MuiMenuItem-root.Mui-selected:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.25),
                },
              },
            },
          }}
        >
          {localBreakouts.length === 0
            ? null
            : localBreakouts.map(breakout => (
                <MenuItem key={breakout.id} value={breakout.id} onClick={e => e.stopPropagation()}>
                  <Typography variant="body2">
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {breakout.breakoutname}
                    </Box>
                    {breakout.isTypical && (
                      <Box
                        component="span"
                        sx={{
                          color: theme.palette.secondary.main,
                          fontWeight: 600,
                          ml: 0.5,
                        }}
                      >
                        [Typical]
                      </Box>
                    )}
                    <Box
                      component="span"
                      sx={{
                        color:
                          theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.white, 0.7)
                            : alpha(theme.palette.common.black, 0.6),
                        ml: 0.5,
                      }}
                    >
                      ({breakout.multiplier}x)
                    </Box>
                  </Typography>
                </MenuItem>
              ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default React.memo(BreakoutSelector);
